import type { Deal } from '@/types';

export type PriorityReason =
  | 'overdue_hot'
  | 'overdue'
  | 'hot_quiet'
  | 'no_next_step'
  | 'stale'
  | 'closing'
  | 'normal';

export const REASON_LABEL: Record<PriorityReason, string> = {
  overdue_hot: 'Hot · overdue',
  overdue: 'Overdue',
  hot_quiet: 'Hot · quiet',
  no_next_step: 'No next step',
  stale: 'Stale',
  closing: 'Closing',
  normal: '',
};

export interface PriorityResult {
  score: number; // higher = more urgent
  reason: PriorityReason;
}

/**
 * Shared priority scoring used by the Next Best Action card and
 * the auto-sorted My Deals list. Higher score = more urgent.
 */
export function scoreDealPriority(
  deal: Deal,
  opts: { staleHours: number; now?: number } = { staleHours: 72 },
): PriorityResult {
  const now = opts.now ?? Date.now();
  const staleMs = opts.staleHours * 60 * 60 * 1000;
  const ageMs = now - new Date(deal.last_activity_at).getTime();
  const isHot = deal.priority === 'hot';
  const overdueHalf = ageMs > staleMs / 2;
  const overdueFull = ageMs > staleMs;
  const noNext = !deal.next_step;

  if (isHot && overdueHalf) return { score: 100 + Math.min(ageMs / staleMs, 5), reason: 'overdue_hot' };
  if (overdueFull) return { score: 80 + Math.min(ageMs / staleMs, 5), reason: 'overdue' };
  if (isHot) return { score: 70, reason: 'hot_quiet' };
  if (noNext && deal.stage !== 'sold') return { score: 55, reason: 'no_next_step' };
  if (overdueHalf) return { score: 45, reason: 'stale' };
  if (deal.stage === 'closing') return { score: 35, reason: 'closing' };
  return { score: 10, reason: 'normal' };
}

export function sortByPriority(
  deals: Deal[],
  staleHours: number,
  now: number = Date.now(),
): Array<Deal & { _reason: PriorityReason }> {
  return [...deals]
    .map((d) => {
      const r = scoreDealPriority(d, { staleHours, now });
      return { ...d, _score: r.score, _reason: r.reason };
    })
    .sort((a, b) => b._score - a._score)
    .map(({ _score, ...rest }) => rest);
}